"use client";
import React, { useEffect, useRef, useState } from "react";
import Image from "next/image";
import heart from "@/public/images/heart.svg";
import FaviconIcon from "@/public/Favicon.svg";
import FaviconIconW from "@/public/Favicon w.svg";
import FacultyCard2 from "@/components/shared/FacultyCard2";
import MainSearchComponent from "@/components/shared/SearchAndFilter/MainSearchComponent";
import { useTranslate } from "@/components/base/providers/TranslateProv";
import { useSearchParams } from "next/navigation";
import { useGetAllSpecialtiesWithFiltersQuery } from "@/redux/features/api/website/websiteApiSlice";
import Loader from "@/components/shared/Loader";
import Pagination from "@/components/shared/Pagination";
import { Faculty } from "@/types/Faculty";
import SearchNotFound from "@/components/shared/SearchNotFound";

export default function SpecialtiesClient() {
  const { locale } = useTranslate();
  const lang = locale as "ar" | "en";

  const [currentPage, setCurrentPage] = useState(1);
  const [filter, setFilter] = useState<string | null>("2");
  const itemsPerPage = 9;

  function useGetAllSearchParams() {
    const searchParams = useSearchParams();
    const params: { [key: string]: string } = {};

    searchParams.forEach((value, key) => {
      params[key] = value;
    });

    return params;
  }

  const { refetch, data, isLoading, error, isFetching } =
    useGetAllSpecialtiesWithFiltersQuery({
      ...useGetAllSearchParams(),
      page: currentPage,
      limit: itemsPerPage,
      nominate_it: filter === "recommended" ? 1 : filter === "2" ? "2" : "0",
    });

  const handlePageChange = (selectedItem: { selected: number }) => {
    setCurrentPage(selectedItem.selected + 1);
    refetch();
  };

  const handleFilterClick = () => {
    const newFilter = filter === "recommended" ? null : "recommended";
    setFilter(newFilter);
    refetch();
  };

  const resultsRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (resultsRef.current) {
      resultsRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [currentPage, filter]);

  return (
    <>
      <div
        className="bg-primaryColor w-[95%] mx-auto max-w-[1520px] rounded-[25px] mt-[18px] pt-10 pb-32 flex flex-col gap-5 mb-36"
        ref={resultsRef}
      >
        <p className="text-36 text-white font-[500] mb-5 text-center">
          {lang === "ar"
            ? "اكتشف أفضل الجامعات والكليات والتخصصات المتاحة."
            : "Join a Rich Journey to Study Abroad!"}
        </p>
        <p className="text-18 text-white font-[500] mb-3 text-center">
          {lang === "ar"
            ? "ابحث عن الجامعات والكليات والتخصصات التي تلبي طموحاتك الأكاديمية."
            : "Search for the best universities and language institutes in the world, review them, and apply."}
        </p>
        <div className="mx-auto -mb-[13rem]">
          <MainSearchComponent activeTab={3} />
        </div>
      </div>
      <div className="container">
        <div className="flex items-center justify-between gap-5">
          <p className="text-20 text-black font-[500]">
            {lang === "ar" ? "نتائج البحث" : "Search Results"}:{" "}
            <span className="text-primaryColor">
              {" "}
              ({data?.data?.data.length}){" "}
            </span>
          </p>
          <div className="flex items-center gap-5">
            <button
              onClick={handleFilterClick}
              className={`text-14 flex items-center gap-3 border rounded-[10px] py-2 px-7 ${
                filter === "recommended"
                  ? "bg-primaryColor text-white"
                  : "border-[#F2F2F2] text-descriptionText"
              }`}
            >
              {lang === "ar" ? "نرشحها" : "Recommend"}
              {filter === "recommended" ? (
                <Image src={FaviconIconW} alt="favicon" width={20} height={20} />
              ) : (
                <Image src={FaviconIcon} alt="favicon" width={20} height={20} />
              )}
            </button>
          </div>
        </div>
        {(isLoading || isFetching) && (
          <div className="flex justify-center items-center h-[300px]">
            <Loader />
          </div>
        )}
        {error && (
          <div className="flex justify-center items-center h-[300px]">
            <p className="text-center text-20 text-red-500 font-[600]">
              {lang === "ar" ? "خطأ في جلب البيانات." : "Error fetching data."}
            </p>
          </div>
        )}
        {!isLoading &&
          !isFetching &&
          !error &&
          (data?.data?.data.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 my-5">
              {data?.data?.data?.map((faculty: Faculty, index: number) => (
                <FacultyCard2
                  university={faculty.university}
                  image={faculty.university?.image}
                  images={faculty.university?.images}
                  location={faculty.university?.map_links}
                  locationTitle={
                    faculty.university?.location
                      ? faculty.university?.location[lang]
                      : ""
                  }
                  name={faculty.university?.name}
                  type={faculty.university?.type}
                  faculty={faculty}
                  specialization_id={faculty?.id}
                  key={index}
                  specialize={true}
                  openFaculty={true}
                />
              ))}
            </div>
          ) : (
            <SearchNotFound />
          ))}
      </div>
      {data?.data?.paginate?.totalPages > 1 && (
        <Pagination
          pageCount={data?.data?.paginate?.totalPages}
          onPageChange={handlePageChange}
          currentPage={currentPage}
        />
      )}
    </>
  );
}
